/**
 * Die Saetze und die Schwellen der Betriebs-Gesundheit — Kriterium R2.6
 * (docs/LOOP-RECHERCHE.md, Konzept docs/KONZEPT-RECHERCHE-SICHTBARKEIT.md § 4.8).
 *
 * WARUM ES DIESES PAKET GIBT: `apps/web` hat keinen Test-Runner. Jede Regel, die dort in einer
 * Komponente steht — welche Zahl in welchen Satz gegossen wird, ab wann ein Ablaufplan als still
 * gilt —, waere damit ungeprueft. Dieselbe Trennung wie bei `@maestro/recherche-stand` und
 * `@maestro/oeffnungszeiten-anzeige`: die Regeln wohnen hier, die Tests liegen in
 * `apps/api/test/gesundheit-sichtbar.test.ts` (die Suite, die in der CI garantiert laeuft),
 * die Kachel rendert nur noch, was hier herauskommt.
 *
 * ══ DER LEITSATZ: BESCHREIBEND, NICHT BEWERTEND ══════════════════════════════════════════════
 * Keine Ampel, kein "kritisch", kein "alles in Ordnung". Diese Funktionen sagen, WAS gemessen
 * wurde — Zahlen, Anteile, Zeitpunkte. Die Einordnung macht der Mensch, der den Betrieb kennt.
 *
 * Daraus folgt eine Regel, die hier ausdruecklich festgehalten wird, weil die naheliegende
 * Alternative ein echter Fehler waere: **eine Quote wird NIE wegen zu weniger Daten verschwiegen.**
 * Stand 06.08.2026 stehen auf Produktion 3 von 3 Handelsregister-Abrufen auf `error` — die
 * Gegenstelle antwortet auf die Suchseite mit HTTP 400. Eine Anzeige, die daraus "zu wenige Daten
 * fuer eine Aussage" macht, verschweigt einen Totalausfall. Deshalb steht die Quote IMMER da,
 * zusammen mit ihrer Grundlage ("3 von 3"), damit niemand 100 % mit 100 von 100 verwechselt.
 */

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * Der Ablaufplan (Sweeps): ab wann gilt er als still?
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Ab welchem Alter des letzten Sweep-Heartbeats die Anzeige "laeuft nicht" meldet — in Stunden.
 *
 * ══ DIE GRUNDLAGE HAT SICH GEAENDERT (07.08.2026) ═══════════════════════════════════════════
 * Diese Zahl beschrieb bis heute die Unpuenktlichkeit des Ablaufplans, nicht seinen Ausfall.
 * Der Ablaufplan ist jetzt der **Cloudflare-Cron-Trigger** (`apps/api/wrangler.toml`,
 * `[triggers] crons = ["0 * * * *"]`) — derselbe Code-Pfad, aber nicht mehr die
 * GitHub-Actions-Warteschlange. Damit ist die Unpuenktlichkeit als Grund weg, und die Schwelle
 * darf wieder das messen, wofuer sie da ist: dass ueberhaupt kein Lauf mehr stattfindet.
 *
 * WARUM DER WECHSEL — GEMESSEN, NICHT BEHAUPTET (07.08.2026, `gh run list --workflow=sweeps.yml
 * --event=schedule --limit 100`, 100 planmaessige Laeufe vom 28.07. bis 07.08., 99 Abstaende):
 *   - kleinster Abstand: 0 h 54
 *   - groesster Abstand: 6 h 16   (06.08. 18:26 UTC -> 07.08. 00:42 UTC)
 *   - Mittel: 2 h 29 — statt der konfigurierten 1 h
 *   - ueber 3 h: 25 von 99 (25 %)   ueber 4 h: 6 (6 %)   ueber 6 h: 1 (1 %)
 * Grund: GitHub-Actions-Zeitplaene sind ausdruecklich "best effort" ("may be delayed during
 * periods of high loads", GitHub-Doku zu `schedule`).
 *
 * VORHER STAND HIER 6 STUNDEN, hergeleitet als "groesster gemessener Abstand (4 h 06) + rund
 * 45 % Sicherheit". Diese Herleitung ist am selben Tag gescheitert: die naechste Messung fand
 * 6 h 16. Eine Schwelle, die dem groessten bisher gesehenen Abstand hinterherlaeuft, ist keine
 * Schwelle — sie waechst mit dem Problem mit. Deshalb wurde die URSACHE beseitigt (zuverlaessiger
 * Ablaufplan) statt die Zahl weiter nach oben gezogen.
 *
 * ══ DIE NEUE HERLEITUNG ═════════════════════════════════════════════════════════════════════
 * 3 Stunden = 3 × der konfigurierte Stundentakt. Die Anzeige meldet "still", wenn mindestens
 * drei planmaessige Laeufe hintereinander ausgeblieben sind — das ist ein Ausfall, kein Zittern.
 * Ein echter Ausfall faellt damit nach 3 statt nach 6+ Stunden auf.
 *
 * EHRLICH DAZUGESAGT — was NICHT belegt ist: Cloudflare dokumentiert fuer Cron-Trigger keine
 * Puenktlichkeitszusage (Seite "Workers · Cron Triggers", 07.08.2026 geprueft: kein Satz zu
 * Verzoegerung oder Garantie). Die 3 sind deshalb ein Toleranzband von drei Takten, KEINE
 * gemessene Streuung. NACHMESSEN, sobald der Cloudflare-Cron ein paar Tage Geschichte hat
 * (der Heartbeat speichert nur den letzten Lauf, sql/122 — messbar sind die Worker-Logs bzw.
 * `system_heartbeats.last_run_at` im Zeitverlauf): liegt die tatsaechliche Streuung wie erwartet
 * bei Minuten, darf die Schwelle weiter runter; laeuft der Cron unerwartet unpuenktlich, gehoert
 * dieselbe Frage noch einmal gestellt — dann aber mit Zahlen.
 *
 * Der GitHub-Workflow bleibt als Rueckfallweg bestehen, aber NUR noch von Hand ausloesbar
 * (`workflow_dispatch`, kein `schedule`) — seine Unpuenktlichkeit liegt damit nicht mehr im
 * regulaeren Pfad und darf diese Schwelle nicht mehr nach oben ziehen.
 */
export const ABLAUFPLAN_STILL_AB_STUNDEN = 3;

/** Ab wann ein `pending`-Abruf als haengend gilt — dieselben 15 Minuten, nach denen der Claim
 *  den Platz wieder freigibt (packages/db/sql/223_claim_wiederfreigabe.sql). Zwei verschiedene
 *  Zahlen waeren eine Falle: die Anzeige haette dann "haengend" genannt, was das System laengst
 *  wieder aufgeraeumt hat, oder umgekehrt. */
export const PENDING_HAENGEND_AB_MINUTEN = 15;

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * Bausteine
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/** Ganzzahliger Prozentanteil; `null`, wenn es keine Grundlage gibt (Division durch 0). */
export function anteilProzent(teil: number, ganzes: number): number | null {
  if (!Number.isFinite(teil) || !Number.isFinite(ganzes) || ganzes <= 0) return null;
  return Math.round((teil / ganzes) * 100);
}

/** "vor 3 Minuten" / "vor 2 Stunden" / "vor 4 Tagen" — grob, absichtlich ohne Bibliothek. */
export function vorWieLange(zeitpunkt: Date, jetzt: Date): string {
  const ms = jetzt.getTime() - zeitpunkt.getTime();
  if (ms < 0) return "gerade eben";
  const minuten = Math.floor(ms / 60_000);
  if (minuten < 1) return "gerade eben";
  if (minuten < 60) return `vor ${minuten} ${minuten === 1 ? "Minute" : "Minuten"}`;
  const stunden = Math.floor(minuten / 60);
  if (stunden < 24) return `vor ${stunden} ${stunden === 1 ? "Stunde" : "Stunden"}`;
  const tage = Math.floor(stunden / 24);
  return `vor ${tage} ${tage === 1 ? "Tag" : "Tagen"}`;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * Handelsregister
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export interface Deckelstand {
  /** Abrufe in der laufenden Stunde (count_recent_handelsregister_lookups, sql/222). */
  verbraucht: number;
  /** Globaler Stundendeckel (HOURLY_CAP, lib/handelsregister-lookup.ts). */
  deckel: number;
}

/** Die Zeile aus dem Kriterium: „x/50 diese Stunde". */
export function deckelAuslastung(stand: Deckelstand): string {
  return `${stand.verbraucht}/${stand.deckel} Abrufe diese Stunde`;
}

export interface Abrufbilanz {
  /** Abrufe der letzten 24 h, die ein Ergebnis haben: found | ambiguous | not_found | error. */
  abgeschlossen: number;
  /** davon `error` (Gegenstelle gestoert ODER Seitenstruktur nicht lesbar). */
  fehler: number;
  /** davon `not_found` (Abruf lief, die Firma steht nicht im Register). */
  nichtGefunden: number;
  /** Zeilen, die der Stundendeckel abgewiesen hat (`skipped`) — nie extern gefragt. */
  abgewiesen: number;
}

/**
 * Fehler- und `not_found`-Quote der letzten 24 Stunden — immer mit der Grundlage daneben.
 *
 * Die `abgewiesen`-Zahl steht bewusst NICHT im Nenner: eine am Deckel abgewiesene Zeile hat die
 * Gegenstelle nie erreicht und sagt nichts ueber die Gesundheit des Abrufs. Sie im Nenner zu
 * fuehren wuerde eine echte Fehlerquote genau dann verduennen, wenn viel los ist.
 */
export function abrufBilanzZeile(b: Abrufbilanz): string {
  if (b.abgeschlossen <= 0) {
    return b.abgewiesen > 0
      ? `Keine abgeschlossenen Abrufe in 24 Stunden; ${b.abgewiesen} am Stundendeckel abgewiesen.`
      : "Keine Abrufe in den letzten 24 Stunden.";
  }
  const fehlerAnteil = anteilProzent(b.fehler, b.abgeschlossen);
  const nichtGefundenAnteil = anteilProzent(b.nichtGefunden, b.abgeschlossen);
  const kern =
    `${b.fehler} von ${b.abgeschlossen} Abrufen mit Fehler (${fehlerAnteil} %), ` +
    `${b.nichtGefunden} ohne Registereintrag (${nichtGefundenAnteil} %) — 24 Stunden.`;
  return b.abgewiesen > 0 ? `${kern} Zusätzlich ${b.abgewiesen} am Stundendeckel abgewiesen.` : kern;
}

/** Hängende Abrufe: seit mehr als PENDING_HAENGEND_AB_MINUTEN im Zustand `pending`. */
export function haengendeZeile(anzahl: number): string {
  if (anzahl <= 0) return `Kein Abruf hängt länger als ${PENDING_HAENGEND_AB_MINUTEN} Minuten.`;
  return anzahl === 1
    ? `1 Abruf steht seit mehr als ${PENDING_HAENGEND_AB_MINUTEN} Minuten auf „läuft".`
    : `${anzahl} Abrufe stehen seit mehr als ${PENDING_HAENGEND_AB_MINUTEN} Minuten auf „läuft".`;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * Wetter / Plattform / Ablaufplan
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/*
 * KEINE eigene Places-Zeile hier — sie gehoert `budgetAnzeige` aus
 * `@maestro/oeffnungszeiten-anzeige` (R2.4). Zwei Saetze ueber dieselbe Zahl waeren zwei
 * Wahrheiten, sobald einer von beiden gepflegt wird und der andere nicht; und diese Zahl haengt
 * an einer harten Ausgabengrenze. Die Kachel ruft dort dieselbe Funktion, die der
 * Oeffnungszeiten-Bereich schon benutzt.
 */

/**
 * Letzter erfolgreicher Abruf einer Quelle (z.B. Bright Sky/DWD beim Wetter).
 * `null` heisst genau das, was dort steht — nicht "kaputt", sondern "noch keiner verzeichnet".
 */
export function letzterErfolgZeile(zeitpunkt: Date | null, jetzt: Date): string {
  if (!zeitpunkt) return "Noch kein erfolgreicher Abruf verzeichnet.";
  return `Letzter erfolgreicher Abruf ${vorWieLange(zeitpunkt, jetzt)}.`;
}

/**
 * Meldungen der Plattform-Ebene (`system_errors` mit `tenant_id IS NULL`).
 *
 * WARUM SIE EINE EIGENE ZAHL SIND UND NICHT IN `errorsLast24h` WANDERN: Unter der Zahl steht im
 * Backoffice die Liste aus `GET /api/system/errors`, und die zeigt ausschliesslich Zeilen DIESES
 * Mandanten (Policy sql/69 — Plattform-Zeilen sind fuer jeden Mandanten unsichtbar, und das soll
 * so bleiben: sie koennen Angaben aus fremden Betrieben enthalten). Eine Summe aus beiden ergaebe
 * eine Zahl, zu der die Liste darunter nicht passt — "5 Fehler" und drei sichtbare Zeilen. Genau
 * solche Widersprueche schicken einen Betreiber auf eine Fehlersuche, die keine ist.
 * Der Satz sagt deshalb ausdruecklich mit, dass diese Meldungen nicht in der Liste stehen.
 */
export function plattformMeldungenZeile(anzahl: number): string {
  if (anzahl <= 0) return "Keine Meldungen auf Plattform-Ebene in 24 Stunden.";
  const kopf = anzahl === 1 ? "1 Meldung" : `${anzahl} Meldungen`;
  return `${kopf} auf Plattform-Ebene in 24 Stunden — betrifft alle Betriebe und steht nicht in der Liste unten.`;
}

/** Ist der Ablaufplan still? Reine Rechnung auf der gemessenen Schwelle (siehe Konstante oben). */
export function ablaufplanStill(letzterLauf: Date | null, jetzt: Date): boolean {
  if (!letzterLauf) return true;
  return jetzt.getTime() - letzterLauf.getTime() > ABLAUFPLAN_STILL_AB_STUNDEN * 60 * 60 * 1000;
}

export function ablaufplanZeile(letzterLauf: Date | null, jetzt: Date): string {
  if (!letzterLauf) return "Kein Lauf verzeichnet.";
  const alter = `Letzter Lauf ${vorWieLange(letzterLauf, jetzt)}.`;
  return ablaufplanStill(letzterLauf, jetzt)
    ? `${alter} Das sind mehr als ${ABLAUFPLAN_STILL_AB_STUNDEN} Stunden — mindestens ein planmäßiger Lauf ist ausgefallen.`
    : alter;
}
